import { AccessLevel, AccountOptions, ProductType } from '@dev/ee-api/ee-api-models';
import { includes, isEmpty } from 'lodash-es';
import { isAccountImpersonated, isDomainCustomBranded } from '@dev/ee-tools';

import { store } from '@shared-store/shared-store';
import { BillingType } from '@dev/ee-api/ee-api-models/billing-type';

declare const window: any;
declare const ee: any;

export const TestDomains = ['app.elasticemail.com', 'localhost'];

export const hasAccessLevel = (acl: AccessLevel | AccessLevel[]): boolean => {
    const accountState = store?.getState()?.account;

    if (
        accountState.accountLoading ||
        isEmpty(accountState.GeneralInfo) ||
        isEmpty(accountState.GeneralInfo.AccountAccessLevel)
    ) {
        return false;
    }

    return includes(accountState.GeneralInfo.AccountAccessLevel, acl);
};

export const isSubaccount = (): boolean => {
    const accountState = store?.getState()?.account;

    if (accountState.accountLoading || isEmpty(accountState.GeneralInfo)) {
        return false;
    }

    return accountState.GeneralInfo.IsSub || false;
};

export const isUser = (): boolean => {
    const accountState = store?.getState()?.account;

    if (accountState.accountLoading || isEmpty(accountState.GeneralInfo)) {
        return false;
    }

    return accountState.GeneralInfo.IsUser || false;
};

export const isTrial = (): boolean => {
    const accountState = store?.getState()?.account;

    if (accountState.accountLoading || isEmpty(accountState.GeneralInfo)) {
        return false;
    }

    return accountState.GeneralInfo.IsTrial || false;
};
export const isReseller = (accountState = store?.getState()?.account): boolean => {
    if (accountState.accountLoading || isEmpty(accountState.AdvancedOptions)) {
        return false;
    }

    return accountState.AdvancedOptions.IsOwnedByReseller || false;
};

export const hasLegacyPlan = (): boolean => {
    const { GeneralInfo, accountLoading } = store?.getState()?.account || {};
    if (accountLoading || isEmpty(GeneralInfo)) {
        return true;
    }
    const hasUnlimitedPlan =
        GeneralInfo?.ProductType === ProductType.Marketing && GeneralInfo?.BillingType === BillingType.Subscription;
    const hasEmailApiPlan =
        GeneralInfo?.ProductType === ProductType.API && GeneralInfo?.BillingType === BillingType.PayAsYouGo;
    return !(hasUnlimitedPlan || hasEmailApiPlan);
};

export const isCustomBranding = (): boolean => isDomainCustomBranded(window.location.hostname);

export const isIntercomEnabled = (): boolean => !isCustomBranding() && !isAccountImpersonated();

export const isProPlan = (accountState = store?.getState()?.account): boolean => {
    if (accountState.accountLoading || isEmpty(accountState.GeneralInfo)) {
        return false;
    }

    return accountState.GeneralInfo.IsPro;
};

/**
 * NOTE: Email API Plans have set price per email, Unlimited not.
 */
export const isOnApiPlan = (): boolean => {
    const accountState = store?.getState()?.account;

    if (accountState.accountLoading || isEmpty(accountState.GeneralInfo)) {
        return false;
    }

    return accountState.GeneralInfo.ProductType === ProductType.API;
};

export const isApiBillingPlan = (): boolean => {
    const accountState = store?.getState()?.account;

    if (accountState.accountLoading || isEmpty(accountState.GeneralInfo)) {
        return false;
    }

    return accountState.GeneralInfo.BillingType === BillingType.PayAsYouGo;
};

export const isDebug = (): boolean => TestDomains.includes(window.location.host);

export const getApiPath = (): string => {
    const config = store?.getState().config;

    if (!config.isStandalone && window.ee) {
        return ee.api.path;
    }

    return APIPATH;
};

export const enableContactFeatures = (): boolean => {
    const accountState = store?.getState()?.account;

    return accountState.GeneralInfo.EnableContactFeatures;
};

export const isAccountActivated = (): boolean => {
    const { GeneralInfo } = store?.getState()?.account || {};
    return GeneralInfo?.Options?.includes(AccountOptions.InstantAccessVerified);
};
